import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { Divider } from '@ds';
import { Chat } from '@features/components/Chat';
import cn from 'classnames';
import CaseTabs from './components/Tabs/CaseTabs';
import Header from './components/Header/Header';
import { getCaseSuffixes, hidden } from '@/utils/helpers';
import { useCaseByIdQuery, useCaseResourcesDataQuery } from '@/queries/case';
import { Case, ServiceType } from '@/api/apis/case';
import { ChatViewPermissions } from '@/permissions';

type CaseOverviewProps = {
  caseId: string;
};

const CaseOverview = ({ caseId }: CaseOverviewProps) => {
  const { checkPermission } = useContext(AuthContext);
  const { data: caseData } = useCaseByIdQuery(caseId) as { data: Case };
  const { data: caseResourcesData } = useCaseResourcesDataQuery(caseId);
  const hasChatPermission = checkPermission(ChatViewPermissions.ViewChat);
  if (!caseData || !caseResourcesData) return null;
  const {
    donorHospitalDetails,
    txCenterDetails,
    donorId,
    donorType,
    status,
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    trialProgram,
    laterality,
    duplicateCaseNumber,
  } = caseData;

  const isDirectAcquisition = caseData.serviceType === ServiceType.DirectAcquisition;
  const caseSuffixes = getCaseSuffixes(
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  );

  return (
    <div className="flex flex-col w-full h-full scroll scroll-sm">
      <Header
        caseId={caseId}
        caseSuffixes={caseSuffixes}
        donorId={donorId}
        donorType={donorType}
        organType={organType}
        donorHospital={donorHospitalDetails}
        txHospital={txCenterDetails}
        trialProgram={trialProgram}
      />
      <Divider />
      <div className="flex 2xl:flex-row flex-1 min-h-0">
        <div className="flex flex-col gap-3 w-full overflow-y-auto min-h-0">
          <CaseTabs key={caseData.caseId} caseInfo={caseData} />
        </div>
        <div
          className={cn(
            'flex flex-col h-full grow w-[350px] shrink-0 bg-base-80',
            hidden(isDirectAcquisition || !hasChatPermission),
          )}
        >
          <div className="flex-none">
            <div className="flex items-center justify-between pt-4 pb-2 px-3">
              <span className="text-l-sb">Chats</span>
            </div>
            <hr className="border-t-1 border-solid border-gray-300" />
          </div>
          {hasChatPermission && (
            <div className="flex-1 min-h-0 flex flex-col w-full relative">
              <Chat caseId={caseData.caseId} caseStatus={status} organType={organType} donorType={donorType} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseOverview;
